<script setup>
import ProductVariantSelector from '~/components/partials/subelements/ProductVariantSelector'
import {toMoney} from '~/utils/toMoney'
import {useAuthStore} from '~/store/auth'
import {useCartStore} from '~/store/cart'
import {getProductDefaultImage} from '~/utils/productUtils'
import Button from '~/components/common/Button.vue'

const props = defineProps({
  product: {
    type: Object,
    default: () => null
  },
  allowAddToCart: {
    type: Boolean,
    default: () => true
  },
  allowViewDetails: {
    type: Boolean,
    default: () => true
  },
  showPrices: {
    type: Boolean,
    default: () => true
  },
  baseProductPath: {
    type: String,
    default: () => null
  }
})

const router = useRouter()
const authStore = useAuthStore()
const cartStore = useCartStore()
const notification = useNotification()
const addItemQty = ref(1)
const selectedVariant = ref(null)
const productVariantSelectorRef = ref()

const hasProductVariants = computed(() => productVariants.value?.length > 0)
const productVariants = computed(() => {
  return props.product?.variants?.map(variant => {
    let priceStr = toMoney(variant.price)
    return {
      ...variant,
      label: `${variant.title} (${priceStr})`
    }
  })
})
const productVariantPrices = computed(() => {
  if (!productVariants.value) return false

  return productVariants.value.reduce((acc, variant) => acc + variant.price, 0) > 0
})

const minProductPrice = computed(() => {
  let currentPrice = props.product.price

  if (hasProductVariants.value) {
    productVariants.value.forEach(variant => {
      if (currentPrice > variant.price && variant.price > 0) {
        currentPrice = variant.price
      }
    })
  }

  return currentPrice
})

const buyBtnLabel = computed(() => {
  if (props.product.only_booking) return 'Reservar'

  return 'Comprar'
})

const mainImage = computed(() => {
  return getProductDefaultImage(props.product)
})

const productPath = computed(() => {
  const currentPath = router.currentRoute.value.fullPath

  if(!props.baseProductPath) {
    return `${currentPath}/${props.product?.slug}`
  }
  return `${props.baseProductPath}/${props.product?.slug}`
})

function variantSelected(variantId) {
  if (!variantId) return

  selectedVariant.value = variantId
  addToCart()
}

const addToCart = ($event) => {
  if (hasProductVariants.value && props.product.variants.length === 1) {
    selectedVariant.value = props.product.variants[0].id
  }

  if (hasProductVariants.value && !selectedVariant.value) {
    productVariantSelectorRef.value.open($event)
    return
  }

  if (hasProductVariants.value && !selectedVariant.value) {
    notification.showSuccess('Debes seleccionar una opción para agregar el producto al carrito')
    return
  }

  const itemToAdd = {
    product: props.product,
    qty: addItemQty.value,
    selected_variant: selectedVariant.value?.id || null
  }

  cartStore.addItem(itemToAdd)

  selectedVariant.value = null
  notification.showSuccess('Producto añadido al carrito')
}
</script>

<template>
  <div v-if="product !== null"
       class="col-span-12 sm:col-span-6 lg:col-span-4 xl:col-span-3 flex flex-col justify-between mb-5">
    <div class="text-center">
      <NuxtLink class="bg-white mb-2" :to="productPath">
        <img class="w-full" :src="mainImage.image" :alt="mainImage.title" loading="lazy"/>
      </NuxtLink>
      <div class="flex flex-col justify-between mt-3">
        <NuxtLink class="" :to="productPath">
          <h2 class="italic text-[1.2rem]"><span class="pr-2 text-xs">
        {{ product.id }}/</span>{{ product.title }}
          </h2>
        </NuxtLink>
        <p v-if="product.sold_out" class="text-center text-2xl  lg:text-3xl font-bold italic justify-end">
          AGOTADO</p>
        <p v-if="product.only_booking && ! product.sold_out"
           class="text-center text-3xl  lg:text-3xl font-bold italic justify-end">SOLO RESERVA</p>
        <template v-if="!product.sold_out && !product.only_booking && props.showPrices">
          <div class="text-center">
            <template v-if="product.price_discount_percent > 0">
              <p class="text-xs italic justify-end line-through">{{
                  toMoney(product.price_original)
                }}</p>
              <p class="text-md font-bold text-red-800 italic justify-end">-{{ product.price_discount_percent }}%</p>
            </template>
            <!--<p class="text-3xl  lg:text-3xl font-bold italic justify-end" v-if="productVariantPrices">desde</p>-->
            <p class="text-3xl  lg:text-3xl font-bold italic justify-end">{{ toMoney(minProductPrice) }}</p>
          </div>
        </template>
      </div>
    </div>
    <div class="flex justify-center gap-3 mt-3">
      <slot name="buttons" :productPath="productPath">
        <Button
          v-if="props.allowViewDetails"
          :to="productPath"
          severity="tertiary"
          label="+ Info"
        />
        <Button
          v-if="!product.sold_out && props.allowAddToCart"
          @click="addToCart"
          severity="primary"
          :label="buyBtnLabel"
        />
      </slot>
    </div>
    <ProductVariantSelector
      ref="productVariantSelectorRef"
      v-if="hasProductVariants && allowAddToCart"
      :title="'Opciones ' + product.title"
      :options="product.variants"
      @input="variantSelected"
    />
  </div>
</template>
